import { useEffect, useState } from 'react';

/**
 * Boolean-`useState` mit localStorage-Persistenz pro Key.
 *
 * Use-Case: List-Filter, die zwischen Page-Reloads / Navigationen erhalten
 * bleiben sollen — z.B. "Archivierte Kunden anzeigen" auf der Kunden-Liste.
 * Der User klickt einmal an, navigiert zur Detail-Seite, kommt zurück, und
 * der Filter ist immer noch aktiv (statt resetted wie bei plain `useState`).
 *
 * Speicherformat: `'1'` für `true`, `'0'` für `false`. Beim Lesen werden
 * legacy `'true'`/`'false'`-Strings ebenfalls akzeptiert (für künftige
 * Format-Migrationen safe).
 *
 * Fallbacks:
 *  - Kein `window` (SSR-Hypothese, hier nicht relevant aber defensiv)
 *  - localStorage wirft (Safari-Privacy-Mode, Storage-Quota voll)
 *  → in beiden Fällen verhalten wir uns wie ein gewöhnlicher useState mit
 *    `defaultValue`, ohne Crash.
 */
export function usePersistedToggle(
  key: string,
  defaultValue = false,
): [boolean, (next: boolean | ((prev: boolean) => boolean)) => void] {
  const [value, setValue] = useState<boolean>(() => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return raw === '1' || raw === 'true';
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, value ? '1' : '0');
    } catch {
      // Storage nicht verfügbar (Privacy-Mode, Quota voll) — silently ignore;
      // der In-Memory-State bleibt für die Session konsistent.
    }
  }, [key, value]);

  return [value, setValue];
}
